import { lazy, Suspense, memo } from "react";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { SEOHead } from "@/components/SEOHead";
import { Breadcrumb } from "@/components/Breadcrumb";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { CLINIC_SAME_AS } from "@/lib/clinicSchema";

// Lazy load non-critical components for better initial load
const Services = lazy(() => import("@/components/Services").then(m => ({ default: m.Services })));
const Results = lazy(() => import("@/components/Results").then(m => ({ default: m.Results })));
const Locations = lazy(() => import("@/components/Locations").then(m => ({ default: m.Locations })));
const Contact = lazy(() => import("@/components/Contact").then(m => ({ default: m.Contact })));

// Deferred components - load after main content
const AIChat = lazy(() => import("@/components/AIChat").then(m => ({ default: m.AIChat })));
const LiveSupportChat = lazy(() => import("@/components/LiveSupportChat").then(m => ({ default: m.LiveSupportChat })));
const FloatingCTABar = lazy(() => import("@/components/FloatingCTABar").then(m => ({ default: m.FloatingCTABar })));

const LeadMagnetSection = lazy(() => import("@/components/LeadMagnetSection").then(m => ({ default: m.LeadMagnetSection })));
const Testimonials = lazy(() => import("@/components/Testimonials").then(m => ({ default: m.Testimonials })));

const ReviewsWidget = lazy(() => import("@/components/ReviewsWidget").then(m => ({ default: m.ReviewsWidget })));
const DutchCitiesSection = lazy(() => import("@/components/DutchCitiesSection").then(m => ({ default: m.DutchCitiesSection })));
const LiveLeadTicker = lazy(() => import("@/components/LiveLeadTicker").then(m => ({ default: m.LiveLeadTicker })));

// Lightweight skeleton loader with content-visibility for mobile
const SectionSkeleton = memo(() => (
  <div className="py-16 md:py-24 animate-pulse content-auto">
    <div className="container mx-auto px-4">
      <div className="h-6 md:h-8 bg-muted rounded w-2/3 md:w-1/3 mx-auto mb-3 md:mb-4"></div>
      <div className="h-4 bg-muted rounded w-3/4 md:w-1/2 mx-auto mb-6 md:mb-8"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-36 md:h-48 bg-muted rounded"></div>
        ))}
      </div>
    </div>
  </div>
));

SectionSkeleton.displayName = 'SectionSkeleton';

const Index = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const isNL = language === 'nl';

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["MedicalClinic", "LocalBusiness"],
    "name": "Hair Art Clinics",
    "url": "https://hairartclinics.net",
    "logo": "https://hairartclinics.net/og-image.jpg",
    "image": "https://hairartclinics.net/og-image.jpg",
    "description": isNL
      ? "Gespecialiseerde haartransplantatie kliniek in Nederland. FUE, DHI en Saffier haartransplantatie."
      : "Specialized hair transplant clinic in the Netherlands. FUE, DHI and Sapphire hair transplant.",
    "telephone": "+31638523131",
    "email": "siostarr@hairartclinics.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Verrijn Stuartlaan 17",
      "addressLocality": "Rijswijk",
      "postalCode": "2288 EK",
      "addressCountry": "NL"
    },
    "geo": { "@type": "GeoCoordinates", "latitude": 52.0705, "longitude": 4.3007 },
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], "opens": "10:00", "closes": "18:00" }
    ],
    "priceRange": "€€€",
    "medicalSpecialty": "Dermatology",
    "availableService": [
      { "@type": "MedicalProcedure", "name": "FUE Haartransplantatie" },
      { "@type": "MedicalProcedure", "name": "DHI Haartransplantatie" },
      { "@type": "MedicalProcedure", "name": "Saffier FUE" },
      { "@type": "MedicalProcedure", "name": "PRP Behandeling" }
    ],
    "sameAs": [...CLINIC_SAME_AS]
  };

  const faqNL = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Wat kost een haartransplantatie in Nederland?", "acceptedAnswer": { "@type": "Answer", "text": "De kosten van een haartransplantatie in Nederland bij Hair Art Clinics starten vanaf €1.500. De uiteindelijke prijs hangt af van het aantal transplantaten en de gekozen techniek (FUE, DHI of Saffier). We bieden een gratis consult aan voor een persoonlijke prijsopgave." } },
      { "@type": "Question", "name": "Wordt een haartransplantatie vergoed door de zorgverzekering?", "acceptedAnswer": { "@type": "Answer", "text": "Een haartransplantatie wordt in de meeste gevallen niet vergoed door de basisverzekering in Nederland, omdat het als cosmetische ingreep wordt beschouwd. In uitzonderingsgevallen bij medisch haaruitval (zoals alopecia) kan aanvullende vergoeding mogelijk zijn. Onze adviseurs helpen u graag met meer informatie." } },
      { "@type": "Question", "name": "Hoe lang duurt een haartransplantatie?", "acceptedAnswer": { "@type": "Answer", "text": "Een haartransplantatie duurt gemiddeld 6 tot 8 uur, afhankelijk van het aantal transplantaten. De ingreep vindt plaats onder lokale verdoving en is vrijwel pijnloos. U kunt dezelfde dag naar huis." } },
      { "@type": "Question", "name": "Wanneer zie ik resultaten na een haartransplantatie?", "acceptedAnswer": { "@type": "Answer", "text": "De eerste nieuwe haren groeien zichtbaar na 3 tot 4 maanden. Het definitieve resultaat is na 12 tot 18 maanden volledig zichtbaar. Meer dan 95% van de getransplanteerde haarzakjes groeit permanent aan." } },
      { "@type": "Question", "name": "Is Hair Art Clinics gecertificeerd in Nederland?", "acceptedAnswer": { "@type": "Answer", "text": "Ja, Hair Art Clinics opereert volledig in overeenstemming met de Nederlandse en Europese medische richtlijnen. Wij brengen u in contact met een partnerkliniek." } }
    ]
  };

  const faqEN = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "How much does a hair transplant cost?", "acceptedAnswer": { "@type": "Answer", "text": "Hair restoration packages at Hair Art Clinics start from €1,500. The final price depends on the number of grafts and the chosen technique (FUE, DHI or Sapphire). We offer a free consultation for a personalized quote." } },
      { "@type": "Question", "name": "How long does recovery take after a hair transplant?", "acceptedAnswer": { "@type": "Answer", "text": "Most patients return to desk work within 3–5 days. Initial healing completes within 7–10 days, and significant new growth is visible from 3–4 months, with final results between 12 and 18 months." } },
      { "@type": "Question", "name": "When will I see results after a hair transplant?", "acceptedAnswer": { "@type": "Answer", "text": "First new hairs grow visibly after 3 to 4 months. The final result is fully visible after 12 to 18 months, with over 95% of grafts growing permanently." } },
      { "@type": "Question", "name": "Is Hair Art Clinics certified?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, Hair Art Clinics operates in full compliance with Dutch and European medical guidelines. We connect you with a partner clinic." } }
    ]
  };

  const faqSchema = isNL ? faqNL : faqEN;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://hairartclinics.net/" }
    ]
  };

  const seoTitle = isNL
    ? "Haartransplantatie Nederland — FUE, DHI"
    : "Hair Transplants in Netherlands & Europe";
  const seoDescription = isNL
    ? "Hair Art Clinics: advies bij FUE-, DHI- en Saffier-haartransplantatie via een ervaren partnerkliniek. Gratis consult in Rijswijk (Den Haag)."
    : "Hair Art Clinics: guidance for FUE, DHI & Sapphire hair restoration with an experienced partner clinic. Free consultation in Rijswijk (The Hague).";
  const seoKeywords = isNL
    ? ["haartransplantatie nederland","haartransplantatie kosten","haarkliniek nederland","fue haartransplantatie","dhi haartransplantatie","haartransplantatie den haag","haartransplantatie amsterdam","haartransplantatie rotterdam","saffier fue nederland","haartransplantatie prijs"]
    : ["hair transplant Netherlands","hair transplant Europe","FUE hair transplant","DHI hair transplant","Sapphire FUE","hair clinic Netherlands","hair transplant Den Haag","hair transplant Amsterdam","hair transplant cost","best hair transplant clinic"];

  const showDutchCities = language === 'nl' || language === 'en';

  return (
    <div className="min-h-screen">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        schemas={[
          { type: 'MedicalClinic' as const, data: localBusinessSchema },
          { type: 'FAQPage' as const, data: faqSchema },
          { type: 'BreadcrumbList' as const, data: breadcrumbSchema }
        ]}
      />
      <Header />
      <Breadcrumb />
      
      {/* Deferred non-critical UI elements */}
      <Suspense fallback={null}>
        <AIChat />
        <LiveSupportChat />
        <FloatingCTABar />
      </Suspense>
      
      <WhatsAppButton />
      
      <div id="hero">
        <Hero />
      </div>

      <Suspense fallback={null}>
        <LiveLeadTicker />
      </Suspense>

      
      
      <div id="services">
        <Suspense fallback={<SectionSkeleton />}>
          <Services />
        </Suspense>
      </div>

      
      <Suspense fallback={<SectionSkeleton />}>
        <LeadMagnetSection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <ReviewsWidget />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <Testimonials />
      </Suspense>

      {showDutchCities && (
        <Suspense fallback={<SectionSkeleton />}>
          <DutchCitiesSection />
        </Suspense>
      )}
      
      <div id="results">
        <Suspense fallback={<SectionSkeleton />}>
          <Results />
        </Suspense>
      </div>
      
      <div id="locations">
        <Suspense fallback={<SectionSkeleton />}>
          <Locations />
        </Suspense>
      </div>
      
      <div id="contact">
        <Suspense fallback={<SectionSkeleton />}>
          <Contact />
        </Suspense>
      </div>
      
      <Footer />
    </div>
  );
};

export default Index;
